import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose, setPropTypes, lifecycle, withState } from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import ReactSelectMaterialUi from 'react-select-material-ui';
import BackButton from '../../components/atoms/BackButton';
import RegisterButton from '../../components/atoms/RegisterButton';
import ImagePreview from '../../components/molecules/ImagePreview';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import listMenuChildTypes from '../../constants/MenuChildType';
import { languages, languageType } from '../../constants/Language';
import enabledStripeBilling from '../../constants/StripeBilling';

const styles = {
  root: {
    position: 'absolute',
    width: '100%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none',
    borderRadius: 4
  },
  mainPaper: {
    position: 'relative',
    margin: '1px 0px 10px',
    padding: '10px 26px 10px 26px'
  },
  pageTitle: {
    margin: '20px 0px 0px',
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  description: {
    color: '#707070',
    margin: '20px 0px 0px'
  },
  subTitle: {
    margin: '20px 0px 0px',
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  divider: {
    margin: '20px 0px'
  },
  detailTable: {
    width: '100%',
    marginTop: 15
  },
  tableCellItem: {
    borderTop: '1px solid #DDDDDD',
    borderBottom: '1px solid #DDDDDD',
    backgroundColor: '#F7F7F7',
    width: 200
  },
  tableCellData: {
    borderTop: '1px solid #DDDDDD',
    borderBottom: '1px solid #DDDDDD'
  },
  textField100: {
    padding: '0px 5px 0px 5px',
    width: '100px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px'
  },
  textFieldLong: {
    padding: '0px 5px 0px 5px',
    width: '500px'
  },
  editTable: {
    width: 'auto'
  },
  tableCell: {
    border: '2px solid #ffffff'
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold'
  },
  tableContainer: {
    marginTop: 15
  },
  buttonsContainer: {
    marginTop: 15
  },
  breakLine: {
    whiteSpace: 'pre-line'
  },
  selectControl: {
    width: '100%'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(state => ({
    name: state.loginInfo.name,
    taxTypes: state.gochiMenus.taxTypes
  })),
  setPropTypes(propTypes),
  withState('isLanguage', 'setIsLanguage', 'ja'),
  lifecycle({
    componentDidMount() {
      this.props.setIsLanguage(this.props.currentLanguage);
    }
  })
);

const Main = enhance(
  ({
    classes,
    backAction,
    editAction,
    roles,
    editConfirm,
    taxTypes,
    reducedTaxMenuPrefix,
    openEditProgress,
    progressOkAction,
    loading,
    resultMessage,
    isLanguage,
    setIsLanguage,
    previewImage,
    menuId,
    menuGenreName,
    menuSituationNames,
    menuUserTargetNames,
    isFukuoka
  }) => {
    let { menuName, description } = editConfirm;

    if (isLanguage !== languageType.ORIGIN) {
      const languageData = editConfirm.otherLanguages.find(
        language => language.language === isLanguage
      );
      if (languageData && languageData.translateType) {
        ({ menuName, description } = languageData.params);
      } else {
        menuName = 'Google Translate で自動的に翻訳されるもの';
        description = 'Google Translate で自動的に翻訳されるもの';
      }
    }
    return (
      <div className={classes.root}>
        <Typography className={classes.title} variant="h6">
          {isFukuoka ? 'コース情報確認' : 'メニュー編集確認'}
        </Typography>
        <ReactSelectMaterialUi
          required
          className={[classes.textField, classes.selectControl]}
          defaultValue={isLanguage}
          value={isLanguage}
          options={languages}
          margin="normal"
          onChange={v => {
            if (isLanguage === v) {
              return;
            }
            setIsLanguage(v);
          }}
        />
        <Table className={classes.detailTable}>
          {(roles.isSecretariat ||
            roles.isChildShop ||
            roles.isGroup ||
            roles.isChildGroup) && (
            <>
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  {isFukuoka ? '教室ID' : '店舗ID'}
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.shopId}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  {isFukuoka ? '教室名' : '店舗名'}
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.shopName}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  {isFukuoka ? 'コースID' : 'メニューID'}
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {menuId}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  {isFukuoka ? 'コース名' : 'メニュー名'}
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {(() => {
                    if (editConfirm.reducedTaxType === 'REDUCED') {
                      return reducedTaxMenuPrefix;
                    }
                    return '';
                  })()}
                  {menuName}
                </TableCell>
              </TableRow>
              {editConfirm.subscription === 1 && enabledStripeBilling === true && (
                <TableRow>
                  <TableCell className={classes.tableCellItem}>
                    購入頻度設定
                  </TableCell>
                  <TableCell className={classes.tableCellData}>
                    {`${editConfirm.subscriptionIntervalCount}${
                      editConfirm.subscriptionInterval === 'WEEK'
                        ? '週間'
                        : 'ヶ月'
                    }1回`}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  {isFukuoka ? 'コース種別' : '種別'}
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {listMenuChildTypes.map(item => {
                    if (parseInt(editConfirm.menuChildType, 10) === item.id) {
                      let itemName = item.name;
                      if (isFukuoka) {
                        if (item.id === 1) {
                          itemName = '通常（教室別）';
                        } else if (item.id === 4) {
                          itemName = 'グループ共通';
                        }
                      }
                      return itemName;
                    }
                    return '';
                  })}
                </TableCell>
              </TableRow>
              {!isFukuoka && (
                <TableRow>
                  <TableCell className={classes.tableCellItem}>
                    メニュー表示
                  </TableCell>
                  <TableCell className={classes.tableCellData}>
                    {editConfirm.shashokuOptionStr}
                  </TableCell>
                </TableRow>
              )}
              {editConfirm.menuChildType !== 3 &&
                editConfirm.menuCombo === 0 &&
                !isFukuoka && (
                  <TableRow>
                    <TableCell className={classes.tableCellItem}>
                      利用方法
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {editConfirm.menuLimitTotal === 1
                        ? '一円単位利用'
                        : '一枚単位利用'}
                    </TableCell>
                  </TableRow>
                )}
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  {isFukuoka ? '金額' : '価格'}
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.price}
                  {`円`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellItem}>税区分</TableCell>
                <TableCell className={classes.tableCellData}>
                  {taxTypes.map(item => {
                    if (
                      editConfirm.taxType &&
                      editConfirm.taxType.toString() === item.id.toString()
                    ) {
                      return item.name;
                    }
                    return '';
                  })}
                </TableCell>
              </TableRow>
              {!isFukuoka && (
                <>
                  <TableRow>
                    <TableCell className={classes.tableCellItem}>
                      軽減税率対象
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {editConfirm.reducedTaxType === 'REDUCED'}
                      {(() => {
                        if (editConfirm.reducedTaxType === 'REDUCED') {
                          return '対象';
                        }
                        return '対象外';
                      })()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.tableCellItem}>
                      ジャンル
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {menuGenreName}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.tableCellItem}>
                      シチュエーション
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {menuSituationNames}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.tableCellItem}>
                      贈り相手
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {menuUserTargetNames}
                    </TableCell>
                  </TableRow>
                </>
              )}
              <TableRow>
                <TableCell className={classes.tableCellItem}>説明</TableCell>
                <TableCell
                  className={`${classes.tableCellData} ${classes.breakLine}`}
                >
                  {description}
                </TableCell>
              </TableRow>
              {!isFukuoka && (
                <TableRow>
                  <TableCell className={classes.tableCellItem}>タグ</TableCell>
                  <TableCell className={classes.tableCellData}>
                    {editConfirm.tagStr}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className={classes.tableCellItem}>画像</TableCell>
                <TableCell className={classes.tableCellData}>
                  <ImagePreview previewImage={previewImage} />
                </TableCell>
              </TableRow>
            </>
          )}
        </Table>
        <div className={classes.buttonsContainer}>
          <BackButton onClickAction={backAction} />
          <RegisterButton onClickAction={editAction} />
        </div>
        <ProgressDialog
          openProgress={openEditProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
